/**
 * Local browser-generated call tones (Web Audio API) — entirely separate from
 * the SIP media path in `lib/sip.ts`. Two things this fixes:
 * - DTMF button presses had no audible feedback (sendDTMF sends the signal
 *   over SIP but doesn't make the browser itself produce a sound).
 * - Outgoing calls had no ringback ("brr… brr…") unless Kavkom happens to
 *   send it as real early media — this is a local fallback so the caller
 *   always hears *something* while waiting for pickup.
 * - Incoming calls made no sound at all: the invite only rendered the widget,
 *   so a call ringing in a background tab went unnoticed.
 */

const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  "1": [697, 1209],
  "2": [697, 1336],
  "3": [697, 1477],
  "4": [770, 1209],
  "5": [770, 1336],
  "6": [770, 1477],
  "7": [852, 1209],
  "8": [852, 1336],
  "9": [852, 1477],
  "*": [941, 1209],
  "0": [941, 1336],
  "#": [941, 1477],
};

let sharedContext: AudioContext | null = null;
let gestureListenerAttached = false;

// An incoming call is the one tone NOT triggered by a click, so it is the one
// that can hit a context the browser suspended for lack of a user gesture.
// Resuming on the next gesture anywhere in the page keeps the *following*
// rings audible instead of leaving the webphone permanently silent.
function resumeOnNextGesture(ctx: AudioContext): void {
  if (gestureListenerAttached || typeof document === "undefined") return;
  gestureListenerAttached = true;
  const resume = () => {
    void ctx.resume();
    document.removeEventListener("pointerdown", resume);
    document.removeEventListener("keydown", resume);
    gestureListenerAttached = false;
  };
  document.addEventListener("pointerdown", resume);
  document.addEventListener("keydown", resume);
}

function getAudioContext(): AudioContext {
  if (!sharedContext) sharedContext = new AudioContext();
  // Browsers suspend a newly-created context until a user gesture resumes it —
  // a button click (which is how most callers of this module are triggered)
  // counts, but resume() is cheap to call defensively either way.
  if (sharedContext.state === "suspended") {
    void sharedContext.resume();
    resumeOnNextGesture(sharedContext);
  }
  return sharedContext;
}

/** Warms up the shared AudioContext from inside a user gesture, so a later
 *  incoming call can ring without needing one of its own. */
export function primeCallTones(): void {
  getAudioContext();
}

/** Plays the standard dual-tone (ITU-T Q.23) for one DTMF digit, ~150ms. */
export function playDtmfTone(digit: string): void {
  const frequencies = DTMF_FREQUENCIES[digit];
  if (!frequencies) return;

  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const duration = 0.15;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.2, now + 0.005);
  gain.gain.setValueAtTime(0.2, now + duration - 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  gain.connect(ctx.destination);

  frequencies.forEach((frequency) => {
    const oscillator = ctx.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + duration);
  });
}

const RINGBACK_TONE_HZ = 440;
const RINGBACK_ON_S = 1.5;
const RINGBACK_OFF_S = 3.5;

/**
 * Starts a looping French-style ringback tone (single 440Hz tone, 1.5s on /
 * 3.5s off) and returns a function that stops it. Call the returned function
 * on cleanup (e.g. once the call is answered, hung up, or unmounted) —
 * leaving it running leaks a recurring `setTimeout` chain.
 */
const RINGTONE_FREQUENCIES = [440, 480];
const RINGTONE_BURST_S = 0.4;
const RINGTONE_BURST_GAP_S = 0.2;
const RINGTONE_CYCLE_S = 3;

/**
 * Starts the incoming-call ringtone — two short 440+480Hz bursts every 3s, the
 * classic "ring… ring… (pause)" cadence — and returns a function that stops it.
 * Same contract as `startRingbackTone`: call the returned function on cleanup,
 * or the recurring `setTimeout` chain keeps ringing forever.
 */
export function startRingtone(): () => void {
  const ctx = getAudioContext();
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const playBurst = (startAt: number) => {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.01);
    gain.gain.setValueAtTime(0.18, startAt + RINGTONE_BURST_S - 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + RINGTONE_BURST_S);
    gain.connect(ctx.destination);

    RINGTONE_FREQUENCIES.forEach((frequency) => {
      const oscillator = ctx.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start(startAt);
      oscillator.stop(startAt + RINGTONE_BURST_S);
    });
  };

  const playCycle = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    playBurst(now);
    playBurst(now + RINGTONE_BURST_S + RINGTONE_BURST_GAP_S);
    timeoutId = setTimeout(playCycle, RINGTONE_CYCLE_S * 1000);
  };

  playCycle();

  return () => {
    stopped = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
}

export function startRingbackTone(): () => void {
  const ctx = getAudioContext();
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const playOnce = () => {
    if (stopped) return;
    const now = ctx.currentTime;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.01);
    gain.gain.setValueAtTime(0.15, now + RINGBACK_ON_S - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + RINGBACK_ON_S);
    gain.connect(ctx.destination);

    const oscillator = ctx.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.value = RINGBACK_TONE_HZ;
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + RINGBACK_ON_S);

    timeoutId = setTimeout(playOnce, (RINGBACK_ON_S + RINGBACK_OFF_S) * 1000);
  };

  playOnce();

  return () => {
    stopped = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
}

/** Quiet waiting-call beep, isolated from the SIP microphone/media streams. */
export function startCallWaitingTone(): () => void {
  const ctx = getAudioContext();
  const gains: GainNode[] = [];
  const beep = () => {
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gains.push(gain);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
    gain.connect(ctx.destination);
    const oscillator = ctx.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.value = 440;
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.15);
    oscillator.onended = () => { gain.disconnect(); gains.splice(gains.indexOf(gain), 1); };
  };
  beep();
  const timer = setInterval(beep, 10000);
  return () => {
    clearInterval(timer);
    gains.forEach((gain) => gain.disconnect());
  };
}
